// The lexicon: a vertical's words for six things, from one table (ADR-0477
// decisions 1a–3a; #2032). A hotel's operator reads *Guests* and *Room Wi-Fi*,
// an apartment building's *Residents* and *Unit Wi-Fi*, on the screens about
// their own site or customer; every other screen, every `/v1` field, reason
// code and server sentence stays neutral.
//
// **The table is the only place a vertical becomes a word** (decision 4a), and
// the only place it becomes a picture (ADR-0484 decisions 2a and 4a). No
// screen tests a vertical's key to choose a code path or a string; it asks
// `term()` and renders what comes back. `lexicon.test.ts` refuses a neutral
// lexicon word written into a page string outside this file.
//
// **Keys are the contract, terms are copy** (decision 2a): adding, removing or
// respelling a key takes an ADR, as a key of ADR-0422's list does; a term is
// reworded under `web/COPY.md`. The table is typed against `VerticalKey` and
// `LexiconKey`, so a vertical or a key with no term does not compile.
import { useScope, useScopeIfAny, type Scope } from "./shell/scope";
import { useEstate, useEstateIfAny, type Lists } from "./shell/estate";
import { useShowsVerticals } from "./shell/verticalPreference";
import { isVerticalKey, type VerticalKey } from "./verticals";
import {
  AccessIcon, AmenityIcon, BadgeIcon, BedIcon, BuildingIcon, ConciergeBellIcon, HandHeartIcon, HotelIcon, HouseHeartIcon,
  HouseWifiIcon, NetworkIcon, OfficeIcon, PortalsIcon, ScannerIcon, ShoppingBagIcon, SitesIcon, StoreIcon, type LucideIcon,
} from "./icons";

/** Decision 1a's six keys, closed. */
export type LexiconKey = "site" | "unit" | "occupant" | "visitor" | "pan" | "portal";

/** A term in its singular and plural, capitalised as it opens a sentence. */
export interface Term { one: string; many: string }

const t = (one: string, many = one): Term => ({ one, many });

/** Today's words: what a screen whose subject has no vertical says (decision 1a's last column). */
export const NEUTRAL: Record<LexiconKey, Term> = {
  site: t("Site", "Sites"),
  unit: t("Unit", "Units"),
  occupant: t("Resident", "Residents"),
  visitor: t("Guest", "Guests"),
  pan: t("Per-unit access"),
  portal: t("Portal", "Portals"),
};

/** Decision 2a's table, the first set of terms. */
export const TERMS: Record<VerticalKey, Record<LexiconKey, Term>> = {
  hospitality: {
    site: t("Property", "Properties"), unit: t("Room", "Rooms"), occupant: t("Guest", "Guests"),
    visitor: t("Day visitor", "Day visitors"), pan: t("Room Wi-Fi"), portal: t("Guest portal", "Guest portals"),
  },
  senior: {
    site: t("Community", "Communities"), unit: t("Apartment", "Apartments"), occupant: t("Resident", "Residents"),
    visitor: t("Family visitor", "Family visitors"), pan: t("Resident Wi-Fi"), portal: t("Visitor portal", "Visitor portals"),
  },
  mdu: {
    site: t("Building", "Buildings"), unit: t("Unit", "Units"), occupant: t("Resident", "Residents"),
    visitor: t("Guest", "Guests"), pan: t("Unit Wi-Fi"), portal: t("Amenity portal", "Amenity portals"),
  },
  corporate: {
    site: t("Office", "Offices"), unit: t("Zone", "Zones"), occupant: t("Employee", "Employees"),
    visitor: t("Visitor", "Visitors"), pan: t("Device networks"), portal: t("Visitor portal", "Visitor portals"),
  },
  retail: {
    site: t("Store", "Stores"), unit: t("Area", "Areas"), occupant: t("Associate", "Associates"),
    visitor: t("Shopper", "Shoppers"), pan: t("Device networks"), portal: t("Shopper portal", "Shopper portals"),
  },
};

export interface TermOptions {
  /** The plural. */
  many?: boolean;
  /** Lower-cased for the middle of a sentence: only the first letter moves, so "Room Wi-Fi" reads "room Wi-Fi". */
  lower?: boolean;
}

/** One key's word for a vertical, or the neutral word where there is none. */
export function term(vertical: VerticalKey | undefined, key: LexiconKey, opts: TermOptions = {}): string {
  const entry = (vertical ? TERMS[vertical] : NEUTRAL)[key];
  const word = opts.many ? entry.many : entry.one;
  return opts.lower ? word.charAt(0).toLowerCase() + word.slice(1) : word;
}

/** A key in braces in a sentence written outside a component — `{unit}`,
 * `{Units}`, `{visitors}` — filled with the screen's word: a capital asks for
 * the word as it opens a sentence, a trailing `s` for its plural. The audit
 * labels and the reason codes' sentences are written this way (#2033), so a
 * module with no hook still speaks the vertical of the screen that shows it. */
export function fillTerms(text: string, word: (key: LexiconKey, opts?: TermOptions) => string = (k, o) => term(undefined, k, o)): string {
  return text.replace(/\{(site|unit|occupant|visitor|pan|portal)(s?)\}/gi, (_, key: string, many: string) =>
    word(key.toLowerCase() as LexiconKey, { many: many === "s", lower: key[0] === key[0]!.toLowerCase() }));
}

/** The keys an icon is drawn for (ADR-0484 decision 1a): today the rail's
 * *Sites*, *Access* and *Portals*. A page that draws an icon for another key
 * adds it here, in every column. */
export type IconKey = Extract<LexiconKey, "site" | "pan" | "portal">;

/** The icon beside a key's word where the screen has no vertical: the set's own
 * (#2043). **Nothing outside this file draws these three** (ADR-0484 decision
 * 4a): a page asks `termIcon()` and draws what comes back. */
export const NEUTRAL_ICONS: Record<IconKey, LucideIcon> = { site: SitesIcon, pan: AccessIcon, portal: PortalsIcon };

/** ADR-0484 decision 5's table, the first set of pictures, changed as copy
 * (5a). Typed against `VerticalKey` and `IconKey`, so a vertical or a key with
 * no picture does not compile. */
export const TERM_ICONS: Record<VerticalKey, Record<IconKey, LucideIcon>> = {
  hospitality: { site: HotelIcon, pan: BedIcon, portal: ConciergeBellIcon },
  senior: { site: HouseHeartIcon, pan: HouseWifiIcon, portal: HandHeartIcon },
  mdu: { site: BuildingIcon, pan: AccessIcon, portal: AmenityIcon },
  corporate: { site: OfficeIcon, pan: NetworkIcon, portal: BadgeIcon },
  retail: { site: StoreIcon, pan: ScannerIcon, portal: ShoppingBagIcon },
};

/** A key's icon in a vertical, or the neutral one. */
export function termIcon(vertical: VerticalKey | undefined, key: IconKey): LucideIcon {
  return (vertical ? TERM_ICONS[vertical] : NEUTRAL_ICONS)[key];
}

/** The vertical a screen speaks, read as decision 3a says: a screen whose
 * subject is one site reads that site's effective vertical; one whose subject
 * is a customer reads the customer's default, else neutral; any other screen
 * reads neutral (ADR-0235 decision 4a). The subject is the scope's narrowest
 * level, and the rows are the estate's, which the scope switcher already reads:
 * this asks for nothing (`useEstate(false)`), so a page costs no request by
 * speaking its vertical, and reads neutral until the estate has answered. A
 * stored key the list no longer carries reads neutral. **Every screen reads
 * neutral for a person who has not turned the vertical's presentation on**
 * (ADR-0489 decision 3a): its words, its icons, its page order and the
 * Overview's default all come from here, so this is the one gate. */
export function useSubjectVertical(): VerticalKey | undefined {
  const { scope } = useScope();
  const { lists } = useEstate(false);
  const shown = useShowsVerticals();
  return shown ? subjectVertical(scope, lists) : undefined;
}

/** Decision 3a's reading, once, for both hooks. Nothing outside this file
 * reads a subject's vertical for presentation: `verticalPreference.test.tsx`
 * refuses a second reader, so none can skip the gate above (ADR-0489 decision
 * 3a). */
function subjectVertical(scope: Scope, lists: Lists | undefined): VerticalKey | undefined {
  if (scope.siteId) {
    const key = lists?.sites.find((s) => s.id === scope.siteId)?.effective_vertical?.key ?? undefined;
    return isVerticalKey(key ?? undefined) ? key as VerticalKey : undefined;
  }
  if (scope.customerId) {
    const key = lists?.customers.find((c) => c.id === scope.customerId)?.default_vertical;
    return isVerticalKey(key ?? undefined) ? key as VerticalKey : undefined;
  }
  return undefined;
}

/** `termIcon()` for the screen's own subject: the picture reads the same
 * vertical as the word (ADR-0484 decision 3a). */
export function useTermIcon(): (key: IconKey) => LucideIcon {
  const vertical = useSubjectVertical();
  return (key) => termIcon(vertical, key);
}

/** `term()` for the screen's own subject. */
export function useTerm(): (key: LexiconKey, opts?: TermOptions) => string {
  const vertical = useSubjectVertical();
  return (key, opts) => term(vertical, key, opts);
}

/** `useTerm()` for a component drawn inside the scope and outside it alike,
 * such as an error banner: outside the scope there is no subject, so it reads
 * neutral, as decision 3a says of a screen with none. */
export function useTermIfScoped(): (key: LexiconKey, opts?: TermOptions) => string {
  const scoped = useScopeIfAny();
  const lists = useEstateIfAny();
  const shown = useShowsVerticals();
  const vertical = scoped && shown ? subjectVertical(scoped.scope, lists) : undefined;
  return (key, opts) => term(vertical, key, opts);
}
